import React, { useEffect, useState } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../../shared/icons/Icons';
import { Card } from '../../../shared/components/ui/Card';
import { Button } from '../../../shared/components/ui/Button';
import { Illustration } from '../../../shared/components/ui/Illustration';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import QuickActionTiles from '../components/QuickActionTiles';
import DeadlineWidget from '../components/DeadlineWidget';
import McpNudgeCard from '../../integrations/components/McpNudgeCard';

const PENDING_ORDER_STATUSES = new Set(['submitted', 'in-progress', 'pending-review']);

interface CaseStatsResponse {
  active: number;
  total: number;
  needsAttention: number;
  closedThisYear: number;
  counties: number;
}

interface OrdersListResponse {
  data: Array<{ status: string }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// STAT CARD & DONUT CHART COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

interface StatCardProps {
  label: string;
  value: number;
  subLabel?: string;
  trend?: number;
  testId?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subLabel, trend, testId }) => {
  const brand = useBrand();

  return (
    <div
      data-testid={testId}
      className="bg-white rounded-xl p-4 shadow-xs border"
      style={{ borderColor: brand.colors.border }}
    >
      <div className="text-sm font-medium mb-1" style={{ color: brand.colors.textLight }}>
        {label}
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-light" style={{ color: brand.colors.secondary }}>
          {value}
        </span>
        {trend !== undefined && (
          <span className="text-sm mb-1" style={{ color: brand.colors.success }}>
            +{trend}%
          </span>
        )}
      </div>
      {subLabel && (
        <div className="text-xs mt-1" style={{ color: brand.colors.textMuted }}>
          {subLabel}
        </div>
      )}
    </div>
  );
};

interface DashboardStatsProps {
  /** Live 30-day deadline count derived from the SAME fetch the Upcoming
   *  Deadlines panel renders (Walter R30 — two widgets counting the same
   *  items must agree). Undefined until the panel's fetch resolves. */
  deadlinesDueSoon?: number;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ deadlinesDueSoon }) => {
  const brand = useBrand();
  const stats = brand.text.dashboard.stats;

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {stats.map(stat => (
        <StatCard
          key={stat.id}
          testId={`stat-${stat.id}`}
          label={stat.label}
          value={stat.id === 'deadlines30' && deadlinesDueSoon !== undefined ? deadlinesDueSoon : stat.value}
          subLabel={stat.subLabel}
          trend={stat.trend}
        />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOMER DASHBOARD PAGE
// ═══════════════════════════════════════════════════════════════════════════

interface CustomerDashboardProps {
  onNavigate: (page: string) => void;
}

const CustomerDashboard: React.FC<CustomerDashboardProps> = ({ onNavigate }) => {
  const brand = useBrand();
  const text = brand.text.dashboard;
  const { get, isAuthenticated } = useApi();

  const [casesCount, setCasesCount] = useState<number | undefined>(undefined);
  const [pendingOrdersCount, setPendingOrdersCount] = useState<number | undefined>(undefined);
  // Fed by DeadlineWidget's fetch (its onDeadlinesChange callback) so the
  // Upcoming Deadlines stat card and the panel count the same records (R30).
  const [deadlinesDueSoon, setDeadlinesDueSoon] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    get<CaseStatsResponse>(`${API_BASE_URL}/api/cases/stats`)
      .then((stats) => { if (!cancelled) setCasesCount(stats.active); })
      .catch(() => { /* leave undefined — badge hidden */ });

    get<OrdersListResponse>(`${API_BASE_URL}/api/orders?perPage=100`)
      .then((res) => {
        if (cancelled) return;
        const pending = res.data.filter((o) => PENDING_ORDER_STATUSES.has(o.status)).length;
        setPendingOrdersCount(pending);
      })
      .catch(() => { /* leave undefined — badge hidden */ });

    return () => { cancelled = true; };
  }, [get, isAuthenticated]);

  return (
    <div>
      {/* Page heading — the route's single top-level heading (R3) */}
      <PageTitle>Dashboard</PageTitle>

      {/* Quick Action Tiles */}
      <QuickActionTiles
        onNavigate={onNavigate}
        casesCount={casesCount}
        pendingOrdersCount={pendingOrdersCount}
      />

      {/* Stats Row — exactly one Upcoming Deadlines metric, no Pending
          Signatures, three cards filling the row evenly (Walter R27/R28/R33) */}
      <DashboardStats deadlinesDueSoon={deadlinesDueSoon} />

      {/* Main + Aside layout */}
      <div className="grid grid-cols-[1fr_320px] gap-6">
        {/* Left column — existing cards */}
        <div className="space-y-6">
        {/* Config-gated MCP discoverability nudge — renders only when GET /api/mcp/config reports enabled */}
        <McpNudgeCard onNavigate={onNavigate} />
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2" style={{ color: brand.colors.secondary }}>
              <Icons.Folder className="w-5 h-5" />
              <span className="font-semibold">{text.recentCasesTitle}</span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center py-12">
            <Illustration type="recentCases" />
            <p className="text-center mt-4" style={{ color: brand.colors.textLight }}>
              {text.recentCasesEmpty}
            </p>
            <p className="text-center text-sm mt-2" style={{ color: brand.colors.textMuted }}>
              {text.recentCasesCTA}{' '}
              <a href="#" style={{ color: brand.colors.link }} className="font-medium">
                {text.recentCasesLink}
              </a>.
            </p>
            <Button
              className="mt-6"
              onClick={() => onNavigate('cases')}
              style={{ backgroundColor: brand.colors.primaryAction }}
            >
              Create New Case
            </Button>
          </div>
        </Card>

        {/* The Order Status (Court Filing) donut panel is intentionally gone
            (Walter R29): its hardcoded counts disagreed with the live tile
            badges above it (the R30 bug — 2 rejected / 5 in progress vs the
            tiles' 3 rejections / 13 pending). */}
        </div>

        {/* Right sidebar — Deadlines */}
        <aside>
          <DeadlineWidget
            onDeadlinesChange={(items) =>
              setDeadlinesDueSoon(items.filter((d) => d.daysRemaining <= 30).length)
            }
          />
        </aside>
      </div>
    </div>
  );
};

export default CustomerDashboard;
